import { ProductType, Totals } from '../common/CommonTypes';
/**
 * `PriceTotal` renders aggregated pricing totals for a set of selected products.
 *
 * Behaviour:
 * - Computes totals using shared `ProductMethods` helpers.
 * - Groups monthly instalment products by number of instalments so multiple
 *   instalment plans can be displayed separately.
 * - Renders optional subscription totals (with recurring messaging) and single
 *   payment totals.
 * - Uses the provided `culture` and the current currency symbol when
 *   formatting amounts.
 */

import './PriceTotal.scss';
import {
  calculateTotals,
  getNoOfInstallments,
  getSelectedVariant,
  getVariantInstalmentTotalPrice
} from '../common/ProductMethods';
import { formatNumber } from '../common/Formatter';
import { useCurrencySymbol } from '../../hooks/useCurrencySymbol';
import { CommonValidation } from '../common/CommonValidation/commonValidation';
import { useTranslation } from 'react-i18next';
import { getSelectedNoOfInstallments } from '../common/HelperMethods';

export type PriceTotalProps = {
  products: ProductType[];
  subscriptionTotalLabel?: string;
  oneOffPaymentLabel?: string;
  hideInstallmentLabel?: boolean;
  culture: string;
};

export type TotalLineProps = {
  label: string;
  priceLabel: string;
  installmentLabel: string;
  oneOffPaymentLabel: string;
};

export type GroupedProducts = {
  installment: number;
  firstInstallmentPrice: number;
  subsequentInstallmentPrice: number;
  monthlySelectedProducts: ProductType[];
};

type Accumulator = {
  [key: number]: GroupedProducts;
};

export const TotalLine = ({
  label,
  priceLabel,
  installmentLabel,
  oneOffPaymentLabel
}: TotalLineProps): JSX.Element => {
  return (
    <div className='price-total-container'>
      <div className='price-total-label'>
        <p className='p1'>{label}</p>
        <p className='label text-muted'>{oneOffPaymentLabel}</p>
      </div>
      <div className='price-total-right'>
        <p className='p1-bold'>{priceLabel}</p>
        <p className='label'>{installmentLabel}</p>
      </div>
    </div>
  );
};

const PriceTotal = ({
  products,
  subscriptionTotalLabel,
  oneOffPaymentLabel,
  hideInstallmentLabel,
  culture
}: PriceTotalProps): JSX.Element => {
  const noOfInstallments = getNoOfInstallments(products);

  const totals: Totals = calculateTotals(products, noOfInstallments);

  //const getNoOfInstallmentsMax = (product: ProductType) => {
  //  return getSelectedNoOfInstallments(product);
  //};

  const installmentPrice = (product: ProductType) => {
    const selectedVariant = getSelectedVariant(product.productVariants);
    const installments = getSelectedNoOfInstallments(product);
    return getVariantInstalmentTotalPrice(
      product.productId,
      selectedVariant,
      installments,
      [],
      product.isVariablePrice
    );
  };

  const { t } = useTranslation();
  const currencySymbol = useCurrencySymbol();

  const groupedInstallment = Object.values(
    products
      .filter((sp) => sp.isSelected)
      .reduce<Accumulator>((acc: Accumulator, p: ProductType) => {
        const installment = getSelectedNoOfInstallments(p);
        const ip = installmentPrice(p);
        if (!acc[installment]) {
          acc[installment] = {
            installment,
            firstInstallmentPrice: 0,
            subsequentInstallmentPrice: 0,
            monthlySelectedProducts: []
          };
        }
        acc[installment].firstInstallmentPrice += ip.firstInstallmentPrice;

        acc[installment].subsequentInstallmentPrice +=
          ip.subsequentInstallmentPrice;

        acc[installment].monthlySelectedProducts.push(p);

        return acc;
      }, {})
  );

  const totalArray = groupedInstallment.map((gp) => {
    return calculateTotals(gp.monthlySelectedProducts, gp.installment);
  });

  const getMonthlyTotalMarkup = (): JSX.Element => {
    if (
      totals.selectedFirstMonthlyPrice === 0 &&
      totals.selectedSubsequentMonthlyPrice === 0
    ) {
      return <></>;
    }

    return (
      <>
        {totalArray.map((totals) => {
          if (
            totals.selectedFirstMonthlyPrice ===
              totals.selectedSubsequentMonthlyPrice &&
            totals.selectedTotalPrice > 0 &&
            totals.selectedSubsequentMonthlyPrice > 0
          ) {
            return (
              <TotalLine
                label={t(
                  'customer-product-page.monthly-payment-label-multiple',
                  {
                    noOfInstallments: totals.noOfInstallments
                  }
                )}
                priceLabel={`${currencySymbol}${formatNumber(
                  totals.selectedSubsequentMonthlyPrice,
                  2,
                  culture
                )}`}
                installmentLabel={''}
                oneOffPaymentLabel=''
                key={`totals-${totals.noOfInstallments}`}
              />
            );
          }

          return (
            <>
              {totals.selectedFirstMonthlyPrice > 0 && (
                <TotalLine
                  label={t(
                    'customer-product-page.monthly-payment-label-single'
                  )}
                  priceLabel={`${currencySymbol}${formatNumber(
                    totals.selectedFirstMonthlyPrice,
                    2,
                    culture
                  )}`}
                  installmentLabel=''
                  oneOffPaymentLabel=''
                  key={`totals-${totals.noOfInstallments}`}
                />
              )}
              {totals.selectedSubsequentMonthlyPrice > 0 && (
                <TotalLine
                  label={t(
                    'customer-product-page.monthly-payment-label-multiple',
                    {
                      noOfInstallments: totals.noOfInstallments - 1
                    }
                  )}
                  priceLabel={`${currencySymbol}${formatNumber(
                    totals.selectedSubsequentMonthlyPrice,
                    2,
                    culture
                  )}`}
                  installmentLabel=''
                  oneOffPaymentLabel=''
                  key={`totals-${totals.noOfInstallments}`}
                />
              )}
            </>
          );
        })}
      </>
    );

    return <></>;
  };

  const getSubscriptionTotal = (): JSX.Element => {
    if (totals.selectedSubscriptionPrice > 0) {
      return (
        <div>
          <TotalLine
            label={
              CommonValidation.isNotEmpty(subscriptionTotalLabel) &&
              subscriptionTotalLabel !== undefined
                ? subscriptionTotalLabel
                : t('customer-product-page.subscription-total')
            }
            priceLabel={
              hideInstallmentLabel !== true
                ? `${currencySymbol}${formatNumber(
                    totals.selectedSubscriptionPrice,
                    2,
                    culture
                  )}/${t('customer-product-page.per-month-text')}`
                : `${currencySymbol}${formatNumber(
                    totals.selectedSubscriptionPrice,
                    2,
                    culture
                  )}`
            }
            installmentLabel={
              hideInstallmentLabel !== true
                ? t('customer-product-page.recurring-payment-until-terminated')
                : ''
            }
            oneOffPaymentLabel={
              CommonValidation.isNotEmpty(oneOffPaymentLabel) &&
              oneOffPaymentLabel !== undefined
                ? oneOffPaymentLabel
                : ''
            }
          />
          <hr />
        </div>
      );
    }

    return <></>;
  };

  const monthlyTotals = getMonthlyTotalMarkup();
  const subscriptionTotals = getSubscriptionTotal();

  return (
    <div>
      {subscriptionTotals}
      {monthlyTotals}
      {totals.selectedSinglePrice > 0 && (
        <TotalLine
          label={t('customer-product-page.single-payment-total')}
          priceLabel={`${currencySymbol}${formatNumber(
            totals.selectedSinglePrice,
            2,
            culture
          )}`}
          installmentLabel=''
          oneOffPaymentLabel=''
        />
      )}
      {totals.selectedSubsequentMonthlyPrice > 0 && (
        <div className='total-container'>
          <TotalLine
            label={t('customer-product-page.total-amount-payable')}
            priceLabel={`${currencySymbol}${formatNumber(
              totals.selectedTotalPrice,
              2,
              culture
            )}`}
            installmentLabel=''
            oneOffPaymentLabel=''
          />
          {totals.selectedSubscriptionPrice > 0 && (
            <p className='label'>
              {t('customer-product-page.subscription-price')}
            </p>
          )}
        </div>
      )}
    </div>
  );
};
export default PriceTotal;
